import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, Plus, Trash2, ChevronLeft } from 'lucide-react';
import type { QuestionType } from '@eduborg/shared';
import { QUESTION_TYPES } from '@eduborg/shared';
import { AppHeader } from '@/components/AppHeader';
import { useAuth } from '@/lib/auth-store';
import {
  useAddOptionMutation,
  useAddQuestionMutation,
  useAssessmentByItemQuery,
  useAssessmentTreeQuery,
  useDeleteOptionMutation,
  useDeleteQuestionMutation,
  useUpdateOptionMutation,
  useUpdateQuestionMutation,
} from '@/hooks/queries/useAssessment';
import type { AssessmentShape, OptionShape, QuestionShape } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Instructor-side assessment editor. Routes here are:
 *   /c/:orgSlug/:courseSlug/assessments/:itemId/edit
 *
 * The route uses the module_item id (not the assessment id) so it lines up
 * with what the CourseDetail tree already has links for.
 */
export function AssessmentEditor() {
  const { state } = useAuth();
  const { orgSlug = '', courseSlug = '', itemId = '' } = useParams();

  if (state.status !== 'authenticated') return null;
  const token = state.token;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40">
      <AppHeader />
      <Inner token={token} orgSlug={orgSlug} courseSlug={courseSlug} itemId={itemId} />
    </div>
  );
}

function Inner({
  token,
  orgSlug,
  courseSlug,
  itemId,
}: {
  token: string;
  orgSlug: string;
  courseSlug: string;
  itemId: string;
}) {
  const byItem = useAssessmentByItemQuery(token, itemId);
  const tree = useAssessmentTreeQuery(token, byItem.data?.assessment.id ?? null);

  if (byItem.isLoading || tree.isLoading) {
    return (
      <div className="container py-10">
        <div className="h-32 animate-pulse rounded-2xl bg-card" />
      </div>
    );
  }
  if (byItem.isError || !byItem.data) {
    return (
      <div className="container py-10 text-center">
        <p className="text-sm text-muted-foreground">Couldn&apos;t load assessment.</p>
      </div>
    );
  }
  if (tree.isError || !tree.data) {
    return (
      <div className="container py-10 text-center">
        <p className="text-sm text-muted-foreground">Couldn&apos;t load questions.</p>
      </div>
    );
  }

  return <Editor token={token} orgSlug={orgSlug} courseSlug={courseSlug} tree={tree.data} />;
}

function Editor({
  token,
  orgSlug,
  courseSlug,
  tree,
}: {
  token: string;
  orgSlug: string;
  courseSlug: string;
  tree: { assessment: AssessmentShape; questions: QuestionShape[] };
}) {
  const { assessment, questions } = tree;

  const addQuestion = useAddQuestionMutation(token, assessment.id);

  return (
    <main className="container py-8">
      <Link
        to={`/c/${orgSlug}/${courseSlug}`}
        className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden /> Back to course
      </Link>

      <header className="mt-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{assessment.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {questions.length} question{questions.length === 1 ? '' : 's'} ·{' '}
            {assessment.totalPoints} {assessment.totalPoints === 1 ? 'pt' : 'pts'} total
            {assessment.timeLimitSeconds !== null
              ? ` · ${Math.round(assessment.timeLimitSeconds / 60)} min`
              : ' · untimed'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/c/${orgSlug}/${courseSlug}/assessments/${assessment.moduleItemId}/take`}
            className="inline-flex rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium hover:bg-secondary"
          >
            Preview as student
          </Link>
        </div>
      </header>

      <section className="mt-8 space-y-3">
        {questions.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card/50 p-10 text-center">
            <p className="text-sm text-muted-foreground">
              No questions yet. Pick a type below to add the first one.
            </p>
          </div>
        ) : (
          questions.map((q, ix) => (
            <QuestionRow
              key={q.id}
              index={ix}
              assessmentId={assessment.id}
              token={token}
              question={q}
            />
          ))
        )}
      </section>

      <AddQuestionBar
        pending={addQuestion.isPending}
        onAdd={(type) =>
          addQuestion.mutate({
            type,
            promptHtml: '',
            points: defaultPointsFor(type),
            payloadJson: defaultPayloadFor(type),
          })
        }
      />
    </main>
  );
}

function QuestionRow({
  index,
  assessmentId,
  token,
  question,
}: {
  index: number;
  assessmentId: string;
  token: string;
  question: QuestionShape;
}) {
  const updateQuestion = useUpdateQuestionMutation(token, assessmentId);
  const deleteQuestion = useDeleteQuestionMutation(token, assessmentId);

  const [draft, setDraft] = useState(question.promptHtml);
  const [points, setPoints] = useState(question.points);

  const commit = (patch: Parameters<typeof updateQuestion.mutate>[0]['patch']) =>
    updateQuestion.mutate({ questionId: question.id, patch });

  return (
    <article className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-sm">
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-foreground">
            Q{index + 1} · {readableType(question.type)}
          </span>
          <p className="text-xs text-muted-foreground">
            {question.autoGradeable
              ? 'Auto-graded by EduBorg'
              : 'Instructor review required (AI suggests, never decides)'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            if (window.confirm('Delete this question? Soft delete — recoverable.')) {
              deleteQuestion.mutate(question.id);
            }
          }}
          aria-label="Delete question"
          className="rounded-lg p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </button>
      </header>

      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== question.promptHtml) commit({ promptHtml: draft });
        }}
        placeholder="Question prompt — supports plain text or HTML"
        rows={Math.max(2, Math.min(8, draft.split('\n').length + 1))}
        className="block w-full resize-y rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
      />

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="inline-flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Points</span>
          <input
            type="number"
            min={0}
            max={1000}
            value={points}
            onChange={(e) => setPoints(Math.max(0, parseInt(e.target.value || '0', 10)))}
            onBlur={() => {
              if (points !== question.points) commit({ points });
            }}
            className="w-20 rounded-md border border-input bg-background px-2 py-1 text-sm outline-none focus:border-ring"
          />
        </label>
      </div>

      {questionUsesOptions(question.type) && (
        <OptionsEditor token={token} assessmentId={assessmentId} question={question} />
      )}
      {questionUsesPayload(question.type) && (
        <PayloadEditor assessmentId={assessmentId} token={token} question={question} />
      )}
    </article>
  );
}

/* ─── Options editor (multiple_choice / multiple_select / true_false / matching / classification / drag_drop / ordering) ─── */

function OptionsEditor({
  token,
  assessmentId,
  question,
}: {
  token: string;
  assessmentId: string;
  question: QuestionShape;
}) {
  const addOption = useAddOptionMutation(token, assessmentId);
  const updateOption = useUpdateOptionMutation(token, assessmentId);
  const deleteOption = useDeleteOptionMutation(token, assessmentId);

  const showsMatchValue = ['matching', 'classification', 'drag_drop'].includes(question.type);
  const allowsManyCorrect = question.type === 'multiple_select';

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Options
      </div>
      <ul className="space-y-1.5">
        {question.options.map((opt) => (
          <OptionRow
            key={opt.id}
            opt={opt}
            showsMatchValue={showsMatchValue}
            onUpdate={(patch) => updateOption.mutate({ optionId: opt.id, patch })}
            onSetCorrect={(value) => {
              // For radio-style (multiple_choice / true_false / matching answer keys), exclusivity is enforced server-side too,
              // but for snappier UI we also flip siblings here.
              if (!allowsManyCorrect && value) {
                question.options
                  .filter((o) => o.id !== opt.id && o.isCorrect)
                  .forEach((o) =>
                    updateOption.mutate({ optionId: o.id, patch: { isCorrect: false } }),
                  );
              }
              updateOption.mutate({ optionId: opt.id, patch: { isCorrect: value } });
            }}
            onDelete={() => deleteOption.mutate({ questionId: question.id, optionId: opt.id })}
          />
        ))}
      </ul>
      <button
        type="button"
        disabled={addOption.isPending}
        onClick={() =>
          addOption.mutate({
            questionId: question.id,
            label: '',
            ...(showsMatchValue && { matchValue: '' }),
          })
        }
        className="inline-flex items-center gap-1 rounded-lg border border-dashed border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-secondary"
      >
        <Plus className="h-3 w-3" aria-hidden /> Add option
      </button>
    </div>
  );
}

function OptionRow({
  opt,
  showsMatchValue,
  onUpdate,
  onSetCorrect,
  onDelete,
}: {
  opt: OptionShape;
  showsMatchValue: boolean;
  onUpdate: (
    patch: Partial<{ label: string; matchValue: string | null; isCorrect: boolean }>,
  ) => void;
  onSetCorrect: (v: boolean) => void;
  onDelete: () => void;
}) {
  const [label, setLabel] = useState(opt.label);
  const [matchValue, setMatchValue] = useState(opt.matchValue ?? '');

  return (
    <li className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={opt.isCorrect}
        onChange={(e) => onSetCorrect(e.target.checked)}
        aria-label="Mark as correct"
        className="h-4 w-4 rounded border-input"
      />
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={() => {
          if (label !== opt.label) onUpdate({ label });
        }}
        placeholder="Option label"
        className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm outline-none focus:border-ring"
      />
      {showsMatchValue && (
        <input
          value={matchValue}
          onChange={(e) => setMatchValue(e.target.value)}
          onBlur={() => {
            if (matchValue !== (opt.matchValue ?? '')) onUpdate({ matchValue });
          }}
          placeholder="Match / target"
          className="w-40 rounded-md border border-input bg-background px-2 py-1 text-sm outline-none focus:border-ring"
        />
      )}
      <button
        type="button"
        onClick={onDelete}
        aria-label="Delete option"
        className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </button>
    </li>
  );
}

/* ─── Payload editor (numeric / short_answer / fill_in_blank / formula) ─── */

function PayloadEditor({
  assessmentId,
  token,
  question,
}: {
  assessmentId: string;
  token: string;
  question: QuestionShape;
}) {
  const updateQuestion = useUpdateQuestionMutation(token, assessmentId);

  const initial = question.payloadJson ? safeParse(question.payloadJson) : null;
  const [draftJson, setDraftJson] = useState(
    question.payloadJson ?? defaultPayloadFor(question.type),
  );

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Grading payload (JSON)
      </div>
      <textarea
        value={draftJson}
        onChange={(e) => setDraftJson(e.target.value)}
        onBlur={() => {
          if (draftJson !== question.payloadJson) {
            updateQuestion.mutate({
              questionId: question.id,
              patch: { payloadJson: draftJson },
            });
          }
        }}
        rows={Math.max(3, Math.min(8, draftJson.split('\n').length))}
        className={cn(
          'block w-full resize-y rounded-lg border border-input bg-background px-3 py-2 font-mono text-xs outline-none focus:border-ring focus:ring-2 focus:ring-ring/30',
          initial === null && question.payloadJson !== null && 'border-destructive',
        )}
        placeholder={defaultPayloadFor(question.type)}
        spellCheck={false}
      />
      <p className="text-xs text-muted-foreground">{payloadHint(question.type)}</p>
    </div>
  );
}

/* ─── Add-question bar ─────────────────────────────────────────────────── */

function AddQuestionBar({
  pending,
  onAdd,
}: {
  pending: boolean;
  onAdd: (type: QuestionType) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section className="mt-6 rounded-2xl border border-dashed border-border bg-card/50 p-3">
      {open ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
          {QUESTION_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              disabled={pending}
              onClick={() => {
                onAdd(t);
                setOpen(false);
              }}
              className="rounded-lg border border-border bg-card px-3 py-2 text-left text-xs font-medium hover:bg-secondary disabled:opacity-60"
            >
              {readableType(t)}
            </button>
          ))}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          {pending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Plus className="h-4 w-4" aria-hidden />
          )}
          Add question
        </button>
      )}
    </section>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────────────── */

function readableType(t: QuestionType): string {
  return t.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function questionUsesOptions(t: QuestionType): boolean {
  return [
    'multiple_choice',
    'multiple_select',
    'true_false',
    'matching',
    'classification',
    'drag_drop',
    'ordering',
  ].includes(t);
}

function questionUsesPayload(t: QuestionType): boolean {
  return ['numeric', 'short_answer', 'fill_in_blank', 'formula', 'code'].includes(t);
}

function defaultPointsFor(t: QuestionType): number {
  switch (t) {
    case 'true_false':
      return 1;
    case 'multiple_choice':
    case 'multiple_select':
      return 2;
    case 'long_answer':
    case 'file_upload':
      return 10;
    default:
      return 4;
  }
}

function defaultPayloadFor(t: QuestionType): string {
  switch (t) {
    case 'numeric':
      return JSON.stringify({ type: 'numeric', expected: 0, tolerance: 0 }, null, 2);
    case 'short_answer':
      return JSON.stringify({ type: 'short_answer', accepted: [] }, null, 2);
    case 'fill_in_blank':
      return JSON.stringify(
        { type: 'fill_in_blank', accepted: [[]], scoring: 'per_blank' },
        null,
        2,
      );
    case 'formula':
      return JSON.stringify({ type: 'formula', expected: '' }, null, 2);
    case 'code':
      return JSON.stringify({ type: 'code' }, null, 2);
    default:
      return '';
  }
}

function payloadHint(t: QuestionType): string {
  switch (t) {
    case 'numeric':
      return '{ "type": "numeric", "expected": 9.81, "tolerance": 0.05 }';
    case 'short_answer':
      return '{ "type": "short_answer", "accepted": ["paris"], "regex": "^paris$" } — case-insensitive, trimmed';
    case 'fill_in_blank':
      return '{ "type": "fill_in_blank", "accepted": [["cell"], ["membrane","plasma membrane"]], "scoring": "per_blank" }';
    case 'formula':
      return '{ "type": "formula", "expected": "a*x + b" } — string-normalised compare for now; symbolic engine in Phase 5';
    case 'code':
      return '{ "type": "code", "expectedStdout": "..." } — code grading runs in Phase-5 sandbox; instructor review until then';
    default:
      return 'Type-specific config — Zod-validated server-side at submission time.';
  }
}

function safeParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
